import { useEffect, useState } from 'react';
import { useGlobalStore } from '@/stores';
import {
    StanBanner,
    StanButton,
    StanDivider,
    StanDropdown,
    StanFilters,
    StanInput,
    StanMultiSelect,
    StanPhoneInput,
    StanTextArea,
} from '@/components';
import { DropdownValue } from '@/components/core-components/StanDropdown/StanDropdown.types';
import { ButtonColor, ButtonSize, ButtonVariant } from '@/components/core-components/types';
import IconLink from '@/assets/icons/ic-link.svg';
import IconHome from '@/assets/icons/ic-home.svg';
import IconPerson from '@/assets/icons/ic-profile.svg';
import IcAlertCircle from '@/assets/icons/ic-alert-circle.svg';
import IcInfoCircle from '@/assets/icons/ic-info-circle.svg';

import { StanFilterConfig, StanFilterValue } from '@/components/core-components/StanFilters/types';

export default function InternalsComponentsPage() {
    const setBreadcrumbs = useGlobalStore(state => state.setBreadcrumbs);

    // Interactive component states
    const [buttonColor, setButtonColor] = useState<DropdownValue>({ label: 'Primary', value: 'primary' });
    const [buttonVariant, setButtonVariant] = useState<DropdownValue | null>(null);
    const [buttonSize, setButtonSize] = useState<DropdownValue>({ label: 'Medium', value: 'md' });
    const [buttonLoading, setButtonLoading] = useState<DropdownValue>({ label: 'False', value: 'false' });
    const [buttonDisabled, setButtonDisabled] = useState<DropdownValue>({ label: 'False', value: 'false' });

    // Showcase input states
    const [basicInputValue, setBasicInputValue] = useState('Basic input');
    const [labelInputValue, setLabelInputValue] = useState('Input with label');
    const [floatingInputValue, setFloatingInputValue] = useState('Input with floating label');
    const [requiredInputValue, setRequiredInputValue] = useState('Required input');
    const [hintInputValue, setHintInputValue] = useState('Input with hint');
    const [errorInputValue, setErrorInputValue] = useState('Invalid input');
    const [disabledInputValue, setDisabledInputValue] = useState('Disabled input');
    const [iconsInputValue, setIconsInputValue] = useState('Input with icons');
    const [emailInputValue, setEmailInputValue] = useState('user@example.com');
    const [passwordInputValue, setPasswordInputValue] = useState('password123');
    const [numberInputValue, setNumberInputValue] = useState('42');

    // Playground input states
    const [inputValue, setInputValue] = useState('Sample text');
    const [inputError, setInputError] = useState<DropdownValue>({ label: 'False', value: 'false' });
    const [inputDisabled, setInputDisabled] = useState<DropdownValue>({ label: 'False', value: 'false' });
    const [inputRequired, setInputRequired] = useState<DropdownValue>({ label: 'False', value: 'false' });

    const [textareaValue, setTextareaValue] = useState('Sample textarea content');
    const [textareaError, setTextareaError] = useState<DropdownValue>({ label: 'False', value: 'false' });
    const [textareaDisabled, setTextareaDisabled] = useState<DropdownValue>({ label: 'False', value: 'false' });

    const [filters, _] = useState<StanFilterConfig[]>([
        {
            id: 'audience-name',
            label: 'Name',
            type: 'text',
        },
        {
            id: 'audience-eid',
            label: 'Email',
            type: 'text',
        },
        {
            id: 'audience-since',
            label: 'Date & Time',
            type: 'date',
        },
        {
            id: 'audience-total-purchases',
            label: 'Purchases',
            type: 'number',
        },
        {
            id: 'audience-total-spent',
            label: 'Spent',
            type: 'number',
        },
        {
            id: 'audience-product-id',
            label: 'Product',
            type: 'select',
            customDropdownReduce: 'id',
            customDropdownOptions: [
                {
                    label: 'Payment Plan',
                    id: 29780,
                    type: 'membership',
                    icon: 'https://assets.stanwith.me/graphics/icons/new/icon-membership.jpg',
                    membershipId: 3930,
                    isAffiliateEligible: false,
                },
                {
                    label: 'Build your Stan Store',
                    id: 29781,
                    type: 'stan-referral-link',
                    icon: 'https://assets.stanwith.me/graphics/device/stan-logo.svg',
                    isAffiliateEligible: false,
                },
                {
                    label: 'Book a 1:1 Call with Me&&asdasdasd',
                    id: 29876,
                    type: 'meeting',
                    icon: 'https://assets.stanwith.me/graphics/icons/new/icon-meeting.jpg',
                    isAffiliateEligible: false,
                },
                {
                    label: 'Get started with this amazing course',
                    id: 29948,
                    type: 'course',
                    icon: 'https://assets.stanwith.me/graphics/icons/new/icon-course.jpg',
                    isAffiliateEligible: false,
                },
                {
                    label: '#459 – DeepSeek, China, OpenAI, NVIDIA, xAI, TSMC, Stargate, and AI Megaclusters',
                    id: 30554,
                    type: 'link',
                    icon: 'https://image-cdn-ak.spotifycdn.com/image/ab6772ab000015be8708435d04c3c1eb9b8b10ea',
                    isAffiliateEligible: false,
                },
                {
                    label: 'Classyfied Episode 1',
                    id: 30555,
                    type: 'link',
                    icon: 'https://i.ytimg.com/vi/79KQ4V5xSQg/hqdefault.jpg',
                    isAffiliateEligible: false,
                },
                {
                    label: 'Best Anime Openings and Endings Music Mix │Full Songs - Spring 2024',
                    id: 30559,
                    type: 'link',
                    icon: 'https://i.ytimg.com/vi/50leN2_T8lo/hqdefault.jpg',
                    isAffiliateEligible: false,
                },
                {
                    label: '#213 - The Google Interview: Luck, LeetCode, and What Actually Matters',
                    id: 30560,
                    type: 'link',
                    icon: 'https://image-cdn-ak.spotifycdn.com/image/ab67656300005f1f2b450e4a6dff37d7ecfd1610',
                    isAffiliateEligible: false,
                },
                {
                    label: "You're Probably Doing Web Performance Wrong  with Sia Karamalegos",
                    id: 30562,
                    type: 'link',
                    icon: 'https://image-cdn-ak.spotifycdn.com/image/ab67656300005f1f3b36148809023d744adeea54',
                    isAffiliateEligible: false,
                },
                {
                    label: '#genZweb3 w/ Kasey Hightower',
                    id: 30564,
                    type: 'link',
                    icon: 'https://image-cdn-ak.spotifycdn.com/image/ab67656300005f1f928e18d1fd88df130c60f98e',
                    isAffiliateEligible: false,
                },
                {
                    label: 'Get my free eBook',
                    id: 30584,
                    type: 'lead-magnet',
                    icon: 'https://assets.stanwith.me/graphics/icons/new/icon-lead-magnet.jpg',
                    isAffiliateEligible: false,
                },
                {
                    label: '1:1 Coaching',
                    id: 30987,
                    type: 'meeting',
                    icon: 'https://assets.stanwith.me/graphics/icons/new/icon-meeting.jpg',
                    isAffiliateEligible: false,
                },
                {
                    label: 'Join My Membership!',
                    id: 30988,
                    type: 'membership',
                    icon: 'https://assets.stanwith.me/graphics/icons/new/icon-membership.jpg',
                    membershipId: 3994,
                    isAffiliateEligible: false,
                },
                {
                    label: 'Join My Membership',
                    id: 30990,
                    type: 'membership',
                    icon: 'https://assets.staging.stanwith.me/beta/msc/26187082/rtydl/icon.png',
                    membershipId: 3995,
                    isAffiliateEligible: true,
                },
                {
                    label: '1:1 Coaching',
                    id: 31659,
                    type: 'meeting',
                    icon: 'https://assets.stanwith.me/graphics/icons/new/icon-meeting.jpg',
                    isAffiliateEligible: false,
                },
                {
                    label: 'Live Webinar',
                    id: 31660,
                    type: 'webinar',
                    icon: 'https://assets.stanwith.me/graphics/icons/new/icon-webinar.jpg',
                    isAffiliateEligible: false,
                },
                {
                    label: 'Live Webinar',
                    id: 32185,
                    type: 'webinar',
                    icon: 'https://assets.stanwith.me/graphics/icons/new/icon-webinar.jpg',
                    isAffiliateEligible: false,
                },
                {
                    label: 'Live Webinar',
                    id: 32187,
                    type: 'webinar',
                    icon: 'https://assets.stanwith.me/graphics/icons/new/icon-webinar.jpg',
                    isAffiliateEligible: false,
                },
                {
                    label: 'Live Webinar',
                    id: 32358,
                    type: 'webinar',
                    icon: 'https://assets.stanwith.me/graphics/icons/new/icon-webinar.jpg',
                    isAffiliateEligible: false,
                },
                {
                    label: 'Digital Download',
                    id: 32472,
                    type: 'digital-download',
                    icon: 'https://assets.stanwith.me/graphics/icons/new/icon-digital-download.jpg',
                    isAffiliateEligible: false,
                },
                {
                    label: 'My 12-week Program',
                    id: 32476,
                    type: 'course',
                    icon: 'https://assets.stanwith.me/graphics/icons/new/icon-course.jpg',
                    isAffiliateEligible: false,
                },
                {
                    label: 'My new title of a product you should buy!!',
                    id: 33185,
                    type: 'digital-download',
                    icon: 'https://assets.staging.stanwith.me/beta/msc/26187082/63lit/icon1745856976650.jpeg',
                    isAffiliateEligible: false,
                },
                {
                    label: 'Resell & Rights',
                    id: 33295,
                    type: 'digital-download',
                    icon: 'https://assets.stanwith.me/graphics/icons/new/icon-digital-download.jpg',
                    isAffiliateEligible: true,
                },
                {
                    label: 'Join Me at Jeef Beef Con',
                    id: 33306,
                    type: 'webinar',
                    icon: 'https://assets.stanwith.me/graphics/icons/new/icon-webinar.jpg',
                    isAffiliateEligible: false,
                },
                {
                    label: 'Fulfillment Yo',
                    id: 33543,
                    type: 'fulfillment',
                    icon: 'https://assets.stanwith.me/graphics/icons/new/icon-fulfillment.jpg',
                    isAffiliateEligible: false,
                },
                {
                    label: 'Get My FREE Guide Now!&',
                    id: 34477,
                    type: 'lead-magnet',
                    icon: 'https://assets.stanwith.me/graphics/icons/new/icon-lead-magnet.jpg',
                    isAffiliateEligible: false,
                },
                {
                    label: 'Payment Plan',
                    id: 34584,
                    type: 'digital-download',
                    icon: 'https://assets.stanwith.me/graphics/icons/new/icon-digital-download.jpg',
                    isAffiliateEligible: false,
                },
                {
                    label: 'Join Me at the Webinar',
                    id: 34956,
                    type: 'webinar',
                    icon: 'https://assets.stanwith.me/graphics/icons/new/icon-webinar.jpg',
                    isAffiliateEligible: false,
                },
                {
                    label: 'Get started with this amazing course',
                    id: 34989,
                    type: 'course',
                    icon: 'https://assets.stanwith.me/graphics/icons/new/icon-course.jpg',
                    isAffiliateEligible: false,
                },
                {
                    label: 'Come join my community',
                    id: 35216,
                    type: 'community',
                    icon: 'https://assets.stanwith.me/graphics/icons/new/icon-community.jpg',
                    isAffiliateEligible: false,
                },
                {
                    label: 'Chat With My AI Twin',
                    id: 35245,
                    type: 'ai-avatar',
                    icon: 'https://assets.stanwith.me/graphics/icons/new/icon-ai-avatar.jpg',
                    isAffiliateEligible: false,
                },
                {
                    label: 'Chat With My AI Twin',
                    id: 35246,
                    type: 'ai-avatar',
                    icon: 'https://assets.stanwith.me/graphics/icons/new/icon-ai-avatar.jpg',
                    isAffiliateEligible: false,
                },
                {
                    label: 'Join Me at the Webinar',
                    id: 35789,
                    type: 'webinar',
                    icon: 'https://assets.stanwith.me/graphics/icons/new/icon-webinar.jpg',
                    isAffiliateEligible: false,
                },
                {
                    label: 'Get my free eBook',
                    id: 30170,
                    type: 'lead-magnet',
                    icon: '',
                    isAffiliateEligible: false,
                },
                {
                    label: 'Digital Download',
                    id: 30171,
                    type: 'digital-download',
                    icon: '',
                    isAffiliateEligible: false,
                },
                {
                    label: 'Get my free eBook',
                    id: 32138,
                    type: 'lead-magnet',
                    icon: '',
                    isAffiliateEligible: false,
                },
                {
                    label: 'Digital Download',
                    id: 32139,
                    type: 'digital-download',
                    icon: '',
                    isAffiliateEligible: false,
                },
            ],
            initFilterValue: {
                operatorCode: 'li',
                value: '29780',
            },
        },
        {
            id: 'audience-active-subscription',
            label: 'Active Subscription',
            type: 'select',
            customDropdownReduce: 'membershipId',
            customDropdownOptions: [
                {
                    label: 'Payment Plan',
                    id: 29780,
                    type: 'membership',
                    icon: 'https://assets.stanwith.me/graphics/icons/new/icon-membership.jpg',
                    membershipId: 3930,
                    isAffiliateEligible: false,
                },
                {
                    label: 'Join My Membership!',
                    id: 30988,
                    type: 'membership',
                    icon: 'https://assets.stanwith.me/graphics/icons/new/icon-membership.jpg',
                    membershipId: 3994,
                    isAffiliateEligible: false,
                },
                {
                    label: 'Join My Membership',
                    id: 30990,
                    type: 'membership',
                    icon: 'https://assets.staging.stanwith.me/beta/msc/26187082/rtydl/icon.png',
                    membershipId: 3995,
                    isAffiliateEligible: true,
                },
            ],
        },
        {
            id: 'audience-tags',
            label: 'Tag',
            type: 'select',
            customDropdownReduce: 'name',
            customDropdownOptions: [],
        },
    ]);
    const [filterValues, setFilterValues] = useState<StanFilterValue[]>([
        {
            id: 'audience-product-id',
            operator: 'li',
            value: '29781',
            type: 'select',
        },
        {
            id: 'audience-name',
            operator: 'li',
            value: 'product',
            type: 'text',
        },
        {
            id: 'audience-total-spent',
            operator: 'eq',
            value: '5',
            type: 'number',
        },
        {
            id: 'audience-since',
            operator: 'gte',
            value: '2025-10-08',
            type: 'date',
        },
        {
            id: 'audience-since',
            operator: 'lte',
            value: '2025-10-23',
            type: 'date',
        },
    ]);

    // Showcase textarea states
    const [basicTextareaValue, setBasicTextareaValue] = useState('Basic textarea content');
    const [labelTextareaValue, setLabelTextareaValue] = useState('Textarea with label');
    const [hintTextareaValue, setHintTextareaValue] = useState('Textarea with hint');
    const [errorTextareaValue, setErrorTextareaValue] = useState('Invalid content');
    const [disabledTextareaValue, setDisabledTextareaValue] = useState('Disabled textarea');
    const [rowsTextareaValue, setRowsTextareaValue] = useState('Large textarea with 8 rows');
    const [maxLengthTextareaValue, setMaxLengthTextareaValue] = useState('Textarea with character limit');
    const [smallTextareaValue, setSmallTextareaValue] = useState('Small textarea');
    const [multiSelectValue, setMultiSelectValue] = useState<string[]>([]);
    const [multiSelectOptions] = useState<string[]>([
        'tag 1',
        'tag 2',
        'tag 3',
        'tag 4',
        'tag 5',
        'tag 6',
        'tag 7',
        'tag 8',
        'tag 9',
        'tag 10',
        'tag 11',
        'tag 12',
        'tag 13',
        'tag 14',
        'tag 15',
        'tag 16',
        'tag 17',
        'tag 18',
        'tag 19',
        'tag 20',
    ]);

    useEffect(() => {
        setBreadcrumbs([
            { title: 'Internals', to: '/internals', active: false, id: 'internals' },
            { title: 'Components', to: '/internals/components', active: true, id: 'components' },
        ]);
    }, [setBreadcrumbs]);

    const buttonColors: ButtonColor[] = ['primary', 'secondary', 'dark', 'destructive'];
    const buttonVariants: (ButtonVariant | undefined)[] = [undefined, 'ghost', 'square', 'outline', 'link'];
    const buttonSizes: ButtonSize[] = ['xs', 'sm', 'md', 'lg', 'xl'];

    const colorOptions = buttonColors.map(color => ({
        label: color.charAt(0).toUpperCase() + color.slice(1),
        value: color,
    }));
    const variantOptions = [
        { label: 'Default', value: '' },
        ...buttonVariants
            .filter(v => v !== undefined)
            .map(variant => ({
                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                label: variant!.charAt(0).toUpperCase() + variant!.slice(1),
                value: variant,
            })),
    ];
    const sizeOptions = buttonSizes.map(size => ({ label: size.toUpperCase(), value: size }));
    const booleanOptions = [
        { label: 'False', value: 'false' },
        { label: 'True', value: 'true' },
    ];

    return (
        <div>
            <div className='space-y-12 p-6'>
                <div>
                    <h1 className='mb-2 text-2xl font-bold'>Component Showcase</h1>
                    <p className='text-gray-600'>Comprehensive display of all component variations</p>
                </div>
                {/* Button Showcase */}
                <section>
                    <h2 className='mb-6 text-xl font-semibold'>StanButton Variations</h2>

                    {/* All Color Variations */}
                    <div className='mb-8'>
                        <h3 className='mb-4 text-lg font-medium'>Colors (Default Size & Variant)</h3>
                        <div className='flex flex-wrap gap-3'>
                            {buttonColors.map(color => (
                                <StanButton
                                    key={color}
                                    name={`showcase-button-${color}`}
                                    color={color}
                                    onClick={() => {}}
                                >
                                    {color.charAt(0).toUpperCase() + color.slice(1)}
                                </StanButton>
                            ))}
                        </div>
                    </div>

                    {/* All Size Variations */}
                    <div className='mb-8'>
                        <h3 className='mb-4 text-lg font-medium'>Sizes (Primary Color)</h3>
                        <div className='flex flex-wrap items-center gap-3'>
                            {buttonSizes.map(size => (
                                <StanButton
                                    key={size}
                                    name={`showcase-button-size-${size}`}
                                    color='primary'
                                    size={size}
                                    onClick={() => {}}
                                >
                                    {size.toUpperCase()}
                                </StanButton>
                            ))}
                        </div>
                    </div>

                    {/* All Variant Variations */}
                    <div className='mb-8'>
                        <h3 className='mb-4 text-lg font-medium'>Variants (Primary Color, Medium Size)</h3>
                        <div className='flex flex-wrap items-center gap-3'>
                            <StanButton name='showcase-button-default' color='primary' onClick={() => {}}>
                                Default
                            </StanButton>
                            {buttonVariants
                                .filter(v => v !== undefined)
                                .map(variant => (
                                    <StanButton
                                        key={variant}
                                        name={`showcase-button-variant-${variant}`}
                                        color='primary'
                                        variant={variant}
                                        onClick={() => {}}
                                    >
                                        {/* eslint-disable-next-line @typescript-eslint/no-non-null-assertion */}
                                        {variant!.charAt(0).toUpperCase() + variant!.slice(1)}
                                    </StanButton>
                                ))}
                        </div>
                    </div>

                    {/* State Variations */}
                    <div className='mb-8'>
                        <h3 className='mb-4 text-lg font-medium'>States</h3>
                        <div className='flex flex-wrap gap-3'>
                            <StanButton name='showcase-button-normal' color='primary' onClick={() => {}}>
                                Normal
                            </StanButton>
                            <StanButton name='showcase-button-loading' color='primary' loading onClick={() => {}}>
                                Loading
                            </StanButton>
                            <StanButton name='showcase-button-disabled' color='primary' disabled onClick={() => {}}>
                                Disabled
                            </StanButton>
                            <StanButton
                                name='showcase-button-with-icon'
                                color='primary'
                                icon={IconHome}
                                onClick={() => {}}
                            >
                                With Icon
                            </StanButton>
                        </div>
                    </div>
                </section>

                <StanDivider padded />

                {/* Input Showcase */}
                <section>
                    <h2 className='mb-6 text-xl font-semibold'>StanFilters</h2>

                    <div className='mb-8 grid grid-cols-1 gap-6'>
                        <StanFilters
                            filters={filters}
                            onFilterChanged={setFilterValues}
                            onResetFilters={() => setFilterValues([])}
                            filterValues={filterValues}
                        />
                        icon:{' '}
                    </div>
                </section>

                <StanDivider padded />

                {/* Input Showcase */}
                <section>
                    <h2 className='mb-6 text-xl font-semibold'>StanInput Variations</h2>

                    <div className='mb-8 grid grid-cols-1 gap-6 md:grid-cols-2'>
                        {/* Basic Input */}
                        <div>
                            <h3 className='mb-3 text-lg font-medium'>Basic Input</h3>
                            <StanInput
                                name='showcase-input-basic'
                                value={basicInputValue}
                                onChange={e => setBasicInputValue(e.target.value)}
                                placeholder='Enter text...'
                            />
                        </div>

                        {/* With Top Label */}
                        <div>
                            <h3 className='mb-3 text-lg font-medium'>With Top Label</h3>
                            <StanInput
                                name='showcase-input-label'
                                value={labelInputValue}
                                onChange={e => setLabelInputValue(e.target.value)}
                                topLabel='Input Label'
                                placeholder='Enter text...'
                            />
                        </div>

                        {/* With Floating Label */}
                        <div>
                            <h3 className='mb-3 text-lg font-medium'>With Floating Label</h3>
                            <StanInput
                                name='showcase-input-floating'
                                value={floatingInputValue}
                                onChange={e => setFloatingInputValue(e.target.value)}
                                innerLabel='Floating Label'
                                placeholder='Enter text...'
                            />
                        </div>

                        {/* Required */}
                        <div>
                            <h3 className='mb-3 text-lg font-medium'>Required</h3>
                            <StanInput
                                name='showcase-input-required'
                                value={requiredInputValue}
                                onChange={e => setRequiredInputValue(e.target.value)}
                                topLabel='Required Field'
                                required
                                placeholder='Enter text...'
                            />
                        </div>

                        {/* With Hint Text */}
                        <div>
                            <h3 className='mb-3 text-lg font-medium'>With Hint Text</h3>
                            <StanInput
                                name='showcase-input-hint'
                                value={hintInputValue}
                                onChange={e => setHintInputValue(e.target.value)}
                                topLabel='Input with Hint'
                                hintText='This is a helpful hint'
                                placeholder='Enter text...'
                            />
                        </div>

                        {/* Error State */}
                        <div>
                            <h3 className='mb-3 text-lg font-medium'>Error State</h3>
                            <StanInput
                                name='showcase-input-error'
                                value={errorInputValue}
                                onChange={e => setErrorInputValue(e.target.value)}
                                topLabel='Error Input'
                                error
                                hintText='This field has an error'
                                placeholder='Enter text...'
                            />
                        </div>

                        {/* Disabled */}
                        <div>
                            <h3 className='mb-3 text-lg font-medium'>Disabled</h3>
                            <StanInput
                                name='showcase-input-disabled'
                                value={disabledInputValue}
                                onChange={e => setDisabledInputValue(e.target.value)}
                                topLabel='Disabled Input'
                                disabled
                                placeholder='Enter text...'
                            />
                        </div>

                        {/* With Icons */}
                        <div>
                            <h3 className='mb-3 text-lg font-medium'>With Icons</h3>
                            <StanInput
                                name='showcase-input-icons'
                                value={iconsInputValue}
                                onChange={e => setIconsInputValue(e.target.value)}
                                topLabel='Input with Icons'
                                startIcon={IconPerson}
                                endIcon={IconLink}
                                placeholder='Enter text...'
                            />
                        </div>

                        {/* Different Types */}
                        <div>
                            <h3 className='mb-3 text-lg font-medium'>Email Type</h3>
                            <StanInput
                                name='showcase-input-email'
                                type='email'
                                value={emailInputValue}
                                onChange={e => setEmailInputValue(e.target.value)}
                                topLabel='Email Input'
                                placeholder='Enter email...'
                            />
                        </div>

                        <div>
                            <h3 className='mb-3 text-lg font-medium'>Password Type</h3>
                            <StanInput
                                name='showcase-input-password'
                                type='password'
                                value={passwordInputValue}
                                onChange={e => setPasswordInputValue(e.target.value)}
                                topLabel='Password Input'
                                placeholder='Enter password...'
                            />
                        </div>

                        <div>
                            <h3 className='mb-3 text-lg font-medium'>Number Type</h3>
                            <StanInput
                                name='showcase-input-number'
                                type='number'
                                value={numberInputValue}
                                onChange={e => setNumberInputValue(e.target.value)}
                                topLabel='Number Input'
                                placeholder='Enter number...'
                            />
                        </div>
                        <div>
                            <h3 className='mb-3 text-lg font-medium'>Phone Type</h3>
                            <StanPhoneInput
                                id='showcase-input-phone'
                                name='showcase-input-phone'
                                value={numberInputValue}
                                onChange={value => setNumberInputValue(value.e164)}
                                label='Number Input'
                                placeholder='Enter number...'
                            />
                        </div>
                    </div>
                </section>

                <StanDivider padded />
                <section>
                    <h2 className='mb-6 text-xl font-semibold'>StanMultiSelect</h2>

                    <div className='mb-8 grid grid-cols-1 gap-6 md:grid-cols-2'>
                        <div>
                            <h3 className='mb-3 text-lg font-medium'>Basic MultiSelect</h3>
                            <StanMultiSelect
                                value={multiSelectValue}
                                options={multiSelectOptions.map(option => ({ id: option, label: option }))}
                                onChange={value => setMultiSelectValue(value)}
                                placeholder='Select options...'
                            />
                        </div>
                    </div>
                </section>

                {/* TextArea Showcase */}
                <section>
                    <h2 className='mb-6 text-xl font-semibold'>StanTextArea Variations</h2>

                    <div className='mb-8 grid grid-cols-1 gap-6 md:grid-cols-2'>
                        {/* Basic TextArea */}
                        <div>
                            <h3 className='mb-3 text-lg font-medium'>Basic TextArea</h3>
                            <StanTextArea
                                id='showcase-textarea-basic'
                                value={basicTextareaValue}
                                onChange={e => setBasicTextareaValue(e.target.value)}
                                placeholder='Enter your text...'
                            />
                        </div>

                        {/* With Label */}
                        <div>
                            <h3 className='mb-3 text-lg font-medium'>With Label</h3>
                            <StanTextArea
                                id='showcase-textarea-label'
                                value={labelTextareaValue}
                                onChange={e => setLabelTextareaValue(e.target.value)}
                                label='Description'
                                placeholder='Enter your description...'
                            />
                        </div>

                        {/* With Hint Text */}
                        <div>
                            <h3 className='mb-3 text-lg font-medium'>With Hint Text</h3>
                            <StanTextArea
                                id='showcase-textarea-hint'
                                value={hintTextareaValue}
                                onChange={e => setHintTextareaValue(e.target.value)}
                                label='Comments'
                                hintText='Please provide detailed comments'
                                placeholder='Enter your comments...'
                            />
                        </div>

                        {/* Error State */}
                        <div>
                            <h3 className='mb-3 text-lg font-medium'>Error State</h3>
                            <StanTextArea
                                id='showcase-textarea-error'
                                value={errorTextareaValue}
                                onChange={e => setErrorTextareaValue(e.target.value)}
                                label='Error TextArea'
                                error
                                hintText='This field has an error'
                                placeholder='Enter your text...'
                            />
                        </div>

                        {/* Disabled */}
                        <div>
                            <h3 className='mb-3 text-lg font-medium'>Disabled</h3>
                            <StanTextArea
                                id='showcase-textarea-disabled'
                                value={disabledTextareaValue}
                                onChange={e => setDisabledTextareaValue(e.target.value)}
                                label='Disabled TextArea'
                                disabled
                                placeholder='Enter your text...'
                            />
                        </div>

                        {/* Custom Rows */}
                        <div>
                            <h3 className='mb-3 text-lg font-medium'>Custom Rows (8)</h3>
                            <StanTextArea
                                id='showcase-textarea-rows'
                                value={rowsTextareaValue}
                                onChange={e => setRowsTextareaValue(e.target.value)}
                                label='Large TextArea'
                                rows={8}
                                placeholder='Enter your text...'
                            />
                        </div>

                        {/* With Max Length */}
                        <div>
                            <h3 className='mb-3 text-lg font-medium'>With Max Length (100)</h3>
                            <StanTextArea
                                id='showcase-textarea-maxlength'
                                value={maxLengthTextareaValue}
                                onChange={e => setMaxLengthTextareaValue(e.target.value)}
                                label='Limited TextArea'
                                maxLength={100}
                                hintText='Maximum 100 characters'
                                placeholder='Enter your text...'
                            />
                        </div>

                        {/* Small Rows */}
                        <div>
                            <h3 className='mb-3 text-lg font-medium'>Small (3 rows)</h3>
                            <StanTextArea
                                id='showcase-textarea-small'
                                value={smallTextareaValue}
                                onChange={e => setSmallTextareaValue(e.target.value)}
                                label='Small TextArea'
                                rows={3}
                                placeholder='Enter your text...'
                            />
                        </div>
                    </div>
                </section>

                <StanDivider padded />

                {/* Interactive Component Playground */}
                <section>
                    <h2 className='mb-6 text-xl font-semibold'>Interactive Component Playground</h2>
                    <p className='mb-6 text-gray-600'>Use the dropdowns below to customize components in real-time</p>

                    <div className='grid grid-cols-1 gap-8 lg:grid-cols-2'>
                        {/* Button Playground */}
                        <div className='space-y-6'>
                            <h3 className='text-lg font-medium'>Button Playground</h3>

                            <div className='grid grid-cols-2 gap-4'>
                                <StanDropdown
                                    id='button-color-dropdown'
                                    name='button-color'
                                    placeholder='Select color'
                                    label='Color'
                                    options={colorOptions}
                                    onSelect={setButtonColor}
                                    currentValue={buttonColor}
                                />

                                <StanDropdown
                                    id='button-variant-dropdown'
                                    name='button-variant'
                                    placeholder='Select variant'
                                    label='Variant'
                                    options={variantOptions}
                                    onSelect={setButtonVariant}
                                    currentValue={buttonVariant}
                                />

                                <StanDropdown
                                    id='button-size-dropdown'
                                    name='button-size'
                                    placeholder='Select size'
                                    label='Size'
                                    options={sizeOptions}
                                    onSelect={setButtonSize}
                                    currentValue={buttonSize}
                                />

                                <StanDropdown
                                    id='button-loading-dropdown'
                                    name='button-loading'
                                    placeholder='Loading state'
                                    label='Loading'
                                    options={booleanOptions}
                                    onSelect={setButtonLoading}
                                    currentValue={buttonLoading}
                                />

                                <StanDropdown
                                    id='button-disabled-dropdown'
                                    name='button-disabled'
                                    placeholder='Disabled state'
                                    label='Disabled'
                                    options={booleanOptions}
                                    onSelect={setButtonDisabled}
                                    currentValue={buttonDisabled}
                                />
                            </div>

                            <div className='rounded-lg border bg-gray-50 p-4'>
                                <StanButton
                                    name='playground-button'
                                    color={buttonColor?.value as ButtonColor}
                                    variant={
                                        buttonVariant?.value === ''
                                            ? undefined
                                            : (buttonVariant?.value as ButtonVariant)
                                    }
                                    size={buttonSize?.value as ButtonSize}
                                    loading={buttonLoading?.value === 'true'}
                                    disabled={buttonDisabled?.value === 'true'}
                                    onClick={() => {
                                        /* Playground button clicked */
                                    }}
                                    icon={IconHome}
                                >
                                    Playground Button
                                </StanButton>
                            </div>
                        </div>

                        {/* Input Playground */}
                        <div className='space-y-6'>
                            <h3 className='text-lg font-medium'>Input Playground</h3>

                            <div className='grid grid-cols-2 gap-4'>
                                <StanDropdown
                                    id='input-error-dropdown'
                                    name='input-error'
                                    placeholder='Error state'
                                    label='Error'
                                    options={booleanOptions}
                                    onSelect={setInputError}
                                    currentValue={inputError}
                                />

                                <StanDropdown
                                    id='input-disabled-dropdown'
                                    name='input-disabled'
                                    placeholder='Disabled state'
                                    label='Disabled'
                                    options={booleanOptions}
                                    onSelect={setInputDisabled}
                                    currentValue={inputDisabled}
                                />

                                <StanDropdown
                                    id='input-required-dropdown'
                                    name='input-required'
                                    placeholder='Required state'
                                    label='Required'
                                    options={booleanOptions}
                                    onSelect={setInputRequired}
                                    currentValue={inputRequired}
                                />
                            </div>

                            <div className='rounded-lg border bg-gray-50 p-4'>
                                <StanInput
                                    name='playground-input'
                                    value={inputValue}
                                    onChange={e => setInputValue(e.target.value)}
                                    topLabel='Playground Input'
                                    placeholder='Enter text...'
                                    error={inputError?.value === 'true'}
                                    disabled={inputDisabled?.value === 'true'}
                                    required={inputRequired?.value === 'true'}
                                    hintText={
                                        inputError?.value === 'true'
                                            ? 'This field has an error'
                                            : 'This is a playground input'
                                    }
                                    startIcon={IconPerson}
                                />
                            </div>
                        </div>

                        {/* TextArea Playground */}
                        <div className='space-y-6 lg:col-span-2'>
                            <h3 className='text-lg font-medium'>TextArea Playground</h3>

                            <div className='grid grid-cols-2 gap-4 md:grid-cols-3'>
                                <StanDropdown
                                    id='textarea-error-dropdown'
                                    name='textarea-error'
                                    placeholder='Error state'
                                    label='Error'
                                    options={booleanOptions}
                                    onSelect={setTextareaError}
                                    currentValue={textareaError}
                                />

                                <StanDropdown
                                    id='textarea-disabled-dropdown'
                                    name='textarea-disabled'
                                    placeholder='Disabled state'
                                    label='Disabled'
                                    options={booleanOptions}
                                    onSelect={setTextareaDisabled}
                                    currentValue={textareaDisabled}
                                />
                            </div>

                            <div className='rounded-lg border bg-gray-50 p-4'>
                                <StanTextArea
                                    id='playground-textarea'
                                    value={textareaValue}
                                    onChange={e => setTextareaValue(e.target.value)}
                                    label='Playground TextArea'
                                    placeholder='Enter your text...'
                                    error={textareaError?.value === 'true'}
                                    disabled={textareaDisabled?.value === 'true'}
                                    hintText={
                                        textareaError?.value === 'true'
                                            ? 'This field has an error'
                                            : 'This is a playground textarea'
                                    }
                                    rows={5}
                                />
                            </div>
                        </div>
                    </div>
                </section>

                {/* StanBanner Showcase */}
                <section>
                    <h2 className='mb-6 text-xl font-semibold'>StanBanner Variations</h2>

                    {/* Warning Banner with CTA */}
                    <div className='mb-8'>
                        <h3 className='mb-4 text-lg font-medium'>Warning Banner with CTA Link</h3>
                        <p className='text-neutral-dimmed mb-4 text-sm'>
                            Desktop: Shows CTA link button. Mobile: Whole banner is clickable.
                        </p>
                        <div className='rounded-lg border bg-gray-50 p-4'>
                            <StanBanner
                                variant='warning'
                                icon={IcAlertCircle}
                                onClick={() => alert('Banner clicked!')}
                                ctaText='Set Up Direct Deposit'
                            >
                                To start selling, set up direct deposit
                            </StanBanner>
                        </div>
                    </div>

                    {/* Info Banner with CTA */}
                    <div className='mb-8'>
                        <h3 className='mb-4 text-lg font-medium'>Info Banner with CTA Link</h3>
                        <div className='rounded-lg border bg-gray-50 p-4'>
                            <StanBanner
                                variant='info'
                                icon={IcInfoCircle}
                                onClick={() => alert('Banner clicked!')}
                                ctaText='Learn More'
                            >
                                This is an informational banner with a call-to-action
                            </StanBanner>
                        </div>
                    </div>

                    {/* Danger Banner with CTA */}
                    <div className='mb-8'>
                        <h3 className='mb-4 text-lg font-medium'>Danger Banner with CTA Link</h3>
                        <div className='rounded-lg border bg-gray-50 p-4'>
                            <StanBanner
                                variant='danger'
                                icon={IcAlertCircle}
                                onClick={() => alert('Banner clicked!')}
                                ctaText='Fix Now'
                            >
                                Action required: Please resolve this issue immediately
                            </StanBanner>
                        </div>
                    </div>

                    {/* Banner with Title and Subtitle */}
                    <div className='mb-8'>
                        <h3 className='mb-4 text-lg font-medium'>Banner with Title and Subtitle</h3>
                        <div className='rounded-lg border bg-gray-50 p-4'>
                            <StanBanner
                                variant='warning'
                                icon={IcAlertCircle}
                                title='Account Setup Required'
                                subtitle='Complete your account setup to start accepting payments'
                                onClick={() => alert('Banner clicked!')}
                                ctaText='Complete Setup'
                            />
                        </div>
                    </div>

                    {/* Banner with Buttons */}
                    <div className='mb-8'>
                        <h3 className='mb-4 text-lg font-medium'>Banner with Action Buttons</h3>
                        <div className='rounded-lg border bg-gray-50 p-4'>
                            <StanBanner
                                variant='info'
                                icon={IcInfoCircle}
                                title='New Feature Available'
                                subtitle='Check out our latest updates and improvements'
                                buttons={[
                                    {
                                        name: 'Learn More',
                                        onClick: () => alert('Learn More clicked'),
                                        variant: 'outline',
                                    },
                                    {
                                        name: 'Dismiss',
                                        onClick: () => alert('Dismiss clicked'),
                                        variant: 'ghost',
                                    },
                                ]}
                            />
                        </div>
                    </div>

                    {/* Simple Text Banner */}
                    <div className='mb-8'>
                        <h3 className='mb-4 text-lg font-medium'>Simple Text Banner</h3>
                        <div className='rounded-lg border bg-gray-50 p-4'>
                            <StanBanner variant='warning' onClick={() => alert('Banner clicked!')}>
                                Simple warning message without icon or CTA
                            </StanBanner>
                        </div>
                    </div>
                </section>
            </div>
        </div>
    );
}
